import { capitalize } from "@mui/material";
import { BubbleText } from "@totalenergiescode/mobility-business-rev-design-system";
import type { TFunction } from "i18next";

import type { FeAccountUsersDetail } from "@/api/graphql/accounts/getAccountUsers/types";
import { getUserRoleDisplayedName } from "@/components/Accounts/list/helper";
import { RolesProfileIcon } from "@/components/shared/RolesProfileIcon/component";
import { isRolesProfileCode } from "@/components/shared/RolesProfileIcon/types";
import type { ProfileType } from "@/types/profile.ts";

import { StyledCellDiv, StyledCellText, StyledIconArrowRight } from "./styles";
import type { TableColumns, TableRows } from "./types";

export const fromAccountUsersToColumns = ({
  tCompanies,
}: {
  tCompanies: TFunction;
}): TableColumns => [
  {
    identifier: "email",
    columnName: tCompanies("companies.list.data.email"),
    displayedByDefault: true,
  },
  {
    identifier: "name",
    columnName: tCompanies("companies.list.data.name"),
    displayedByDefault: true,
  },
  {
    identifier: "accountProfile",
    columnName: tCompanies("companies.list.data.accountProfile"),
    displayedByDefault: true,
  },
  {
    identifier: "status",
    columnName: tCompanies("companies.list.data.status"),
    displayedByDefault: true,
  },
  {
    identifier: "createdTs",
    columnName: tCompanies("companies.list.data.createdTs"),
    displayedByDefault: true,
  },
  {
    identifier: "lastConnection",
    columnName: tCompanies("companies.list.data.lastConnection"),
    displayedByDefault: true,
  },
  {
    identifier: "action",
    columnName: "",
    displayedByDefault: true,
  },
];

export const fromAccountUsersToRows = ({
  accountUsers,
  selectedIds,
  onNavigate,
  formattedDate,
  tCompanies,
  tProfiles,
}: {
  accountUsers: FeAccountUsersDetail[];
  selectedIds: Set<string>;
  onNavigate: (userId: string) => void;
  formattedDate: (ts: number | undefined | null) => string;
  tCompanies: TFunction;
  tProfiles: TFunction;
}): TableRows =>
  accountUsers.map((accountUser) => ({
    id: accountUser.id,
    key: accountUser.id,
    selected: selectedIds.has(accountUser.id),
    onRowClick: () => onNavigate(accountUser.userId),
    data: {
      email: accountUser.userEmail,
      name: `${accountUser.userFirstName} ${accountUser.userLastName}`,
      accountProfile: (
        <StyledCellDiv>
          {isRolesProfileCode(accountUser.subscription.roleCode) ? (
            <RolesProfileIcon
              code={accountUser.subscription.roleCode as ProfileType}
              size={28}
            />
          ) : null}
          <StyledCellText variant='text2'>
            {getUserRoleDisplayedName({
              code: accountUser.subscription.roleCode,
              name: accountUser.subscription.roleName,
              t: tProfiles,
            })}
          </StyledCellText>
        </StyledCellDiv>
      ),
      status: (
        <BubbleText
          text={capitalize(
            tCompanies(
              `companies.list.status.${accountUser.status}.label`.toLowerCase(),
            ),
          )}
          variant={
            accountUser.status.toLowerCase() === "active" ? "green" : "grey"
          }
        />
      ),
      createdTs: formattedDate(accountUser.userCreatedTs),
      lastConnection: formattedDate(accountUser.lastUsedTs),
      action: <StyledIconArrowRight />,
    },
  }));
